'use client';

import { type Dispatch, type SetStateAction, useState } from 'react';

import { useSearchParams } from 'next/navigation';

import { parsePanel, useProjectResponseModeContext } from '@/shared-survey';
import { stopAudioPlayback } from '@/shared-ui';
import { useInactivityTimeout } from '@/shared-utilities';
import type {
  GetProjectQuery,
  InitiateSurveyV3Mutation,
} from '@/survey-graphql';

import { useAudioQueueController } from './project-response-audio-controller.hooks';
import {
  useChannel,
  useResponderEmail,
} from './project-response-insight-info.hooks';
import { useProjectResponse } from './project-response.hooks';
import { useHandsFreeAudioLevel } from './project-usability-handsfree-orchestration.hooks';
import {
  useHandsFreeAutoListen,
  useHandsFreeOnboarding,
} from './project-usability-handsfree.hooks';
import {
  useUsabilityProjectResponse,
  useUsabilityRecording,
} from './project-usability-recording.hooks';

interface HandsFreeContentSessionOptions {
  project: GetProjectQuery['project'];
  sessionId: string;
  firstQuestion: InitiateSurveyV3Mutation['initiateSurveyV3']['firstQuestion'];
  isMobileMode: boolean;
}

type ProjectResponse = ReturnType<typeof useProjectResponse>;
type UsabilityRecording = ReturnType<typeof useUsabilityRecording>;

export interface HandsFreeContentSession
  extends
    Pick<
      ProjectResponse,
      | 'isPublished'
      | 'isSaving'
      | 'isSubmitting'
      | 'messages'
      | 'insightLoading'
      | 'isConversationOver'
      | 'handleConversationOver'
      | 'getCompletionRedirectUrl'
      | 'insightId'
      | 'conversationOverRef'
      | 'activityStore'
      | 'progress'
      | 'replyFeedbackIds'
      | 'resetAudioRecord'
      | 'showSubmittedScreen'
    >,
    Pick<
      ReturnType<typeof useUsabilityProjectResponse>,
      'contentUrl' | 'mobileAppEmbedUrl' | 'handleSubmitWithSidebar'
    >,
    Pick<
      UsabilityRecording,
      | 'isRecording'
      | 'isFinalizingRecording'
      | 'isUploadStalled'
      | 'retryUpload'
      | 'uploadProgress'
      | 'endRecording'
      | 'isPermissionDenied'
      | 'isRequestingPermission'
      | 'updateActivity'
      | 'pauseRecording'
      | 'resumeRecording'
      | 'isScreenShareMuted'
    >,
    Pick<
      ReturnType<typeof useInactivityTimeout>,
      | 'isPausedByTimeout'
      | 'showTimeoutOverlay'
      | 'currentRemainingTime'
      | 'handleResumeRecording'
    >,
    Pick<
      ReturnType<typeof useHandsFreeOnboarding>,
      | 'isOnboardingActive'
      | 'currentOnboardingStep'
      | 'handleOnboardingEnd'
      | 'handleOnboardingNext'
    > {
  isUserPaused: boolean;
  setIsUserPaused: Dispatch<SetStateAction<boolean>>;
  showTranscriptError: boolean;
  setShowTranscriptError: Dispatch<SetStateAction<boolean>>;
  isHelpExpanded: boolean;
  setIsHelpExpanded: Dispatch<SetStateAction<boolean>>;
  audioLevel: number;
  isAiVoiceEnabled: boolean;
  recordingError: UsabilityRecording['error'];
  startRecording: UsabilityRecording['start'];
  stopRecording: UsabilityRecording['stop'];
  isCurrentlyPaused: boolean;
  effectivePaused: boolean;
}

export function useHandsFreeContentSession({
  project,
  sessionId,
  firstQuestion,
  isMobileMode,
}: HandsFreeContentSessionOptions): HandsFreeContentSession {
  const searchParams = useSearchParams();
  const responderEmail = useResponderEmail();
  const channel = useChannel();
  const panel = parsePanel(searchParams);

  const [isUserPaused, setIsUserPaused] = useState(false);
  const [showTranscriptError, setShowTranscriptError] = useState(false);
  const [isHelpExpanded, setIsHelpExpanded] = useState(false);

  const { responseMode, audioLevelObservable } =
    useProjectResponseModeContext();

  const audioLevel = useHandsFreeAudioLevel(audioLevelObservable);

  const {
    isPublished,
    isSaving,
    isSubmitting,
    messages,
    handleSubmit,
    insightLoading,
    isConversationOver,
    handleConversationOver,
    getCompletionRedirectUrl,
    insightId,
    conversationOverRef,
    activityStore,
    progress,
    replyFeedbackIds,
    resetAudioRecord,
    showSubmittedScreen,
  } = useProjectResponse({
    project,
    responderEmail,
    channel,
    sessionId,
    firstQuestion,
    panel,
    onTranscriptAnswerError: () => setShowTranscriptError(true),
    enableSubmittedScreen: true,
  });

  const isAiVoiceEnabled = project?.isAiVoiceEnabled ?? false;

  const { contentUrl, mobileAppEmbedUrl, handleSubmitWithSidebar } =
    useUsabilityProjectResponse({
      firstQuestion,
      handleSubmit: async (values) => {
        stopAudioPlayback();
        updateActivity();
        return handleSubmit(values);
      },
    });

  const {
    isRecording,
    isFinalizingRecording,
    isUploadStalled,
    retryUpload,
    uploadProgress,
    error: recordingError,
    start: startRecording,
    stop: stopRecording,
    endRecording,
    isPermissionDenied,
    isRequestingPermission,
    updateActivity,
    getRemainingTime,
    isTimedOut,
    pauseRecording,
    resumeRecording,
    isPaused,
    isScreenShareMuted,
  } = useUsabilityRecording({
    insightId,
    isAiVoiceEnabled,
    skipLocalMic: isMobileMode,
  });

  const {
    isPausedByTimeout,
    showTimeoutOverlay,
    currentRemainingTime,
    handleResumeRecording,
  } = useInactivityTimeout({
    isRecording,
    isPaused: isPaused || isUserPaused,
    getRemainingTime,
    isTimedOut,
    pauseRecording,
    resumeRecording,
    updateActivity,
  });

  const {
    isOnboardingActive,
    currentOnboardingStep,
    handleOnboardingEnd,
    handleOnboardingNext,
  } = useHandsFreeOnboarding({ isRecording, enabled: !isMobileMode });

  const isCurrentlyPaused = isPaused || isUserPaused || isPausedByTimeout;
  const effectivePaused = isCurrentlyPaused || isOnboardingActive;

  useAudioQueueController(isAiVoiceEnabled, responseMode, effectivePaused);

  useHandsFreeAutoListen({
    enabled: !isMobileMode,
    isAiVoiceEnabled,
    isPaused: effectivePaused || isConversationOver,
  });

  return {
    isUserPaused,
    setIsUserPaused,
    showTranscriptError,
    setShowTranscriptError,
    isHelpExpanded,
    setIsHelpExpanded,
    audioLevel,
    isPublished,
    isSaving,
    isSubmitting,
    messages,
    insightLoading,
    isConversationOver,
    handleConversationOver,
    getCompletionRedirectUrl,
    insightId,
    conversationOverRef,
    activityStore,
    progress,
    replyFeedbackIds,
    resetAudioRecord,
    showSubmittedScreen,
    isAiVoiceEnabled,
    contentUrl,
    mobileAppEmbedUrl,
    handleSubmitWithSidebar,
    isRecording,
    isFinalizingRecording,
    isUploadStalled,
    retryUpload,
    uploadProgress,
    recordingError,
    startRecording,
    stopRecording,
    endRecording,
    isPermissionDenied,
    isRequestingPermission,
    updateActivity,
    pauseRecording,
    resumeRecording,
    isScreenShareMuted,
    isPausedByTimeout,
    showTimeoutOverlay,
    currentRemainingTime,
    handleResumeRecording,
    isOnboardingActive,
    currentOnboardingStep,
    handleOnboardingEnd,
    handleOnboardingNext,
    isCurrentlyPaused,
    effectivePaused,
  };
}
